import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { 
  collection, 
  query,
  where,
  getDocs,
  orderBy,
  addDoc,
  doc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import './PlaygroundPage.css';
import { triggerPlaygroundRefinement } from '../utils/cloudFunctionUrls';

// Simple markdown renderer (install react-markdown for better rendering: npm install react-markdown)
const SimpleMarkdownRenderer = ({ children }) => {
  const text = typeof children === 'string' ? children : String(children);
  const lines = text.split('\n');
  const elements = [];
  
  lines.forEach((line, index) => {
    if (line.startsWith('# ')) {
      elements.push(<h1 key={index} style={{ color: '#f6c12b', marginTop: '1rem' }}>{line.substring(2)}</h1>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={index} style={{ color: '#f6c12b', marginTop: '0.75rem' }}>{line.substring(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={index} style={{ color: '#f6c12b', marginTop: '0.5rem' }}>{line.substring(4)}</h3>);
    } else if (line.startsWith('- ')) {
      elements.push(<li key={index} style={{ marginLeft: '1.5rem', marginBottom: '0.25rem' }}>{line.substring(2)}</li>);
    } else if (line.trim() === '') {
      elements.push(<br key={index} />);
    } else if (line.includes('**')) {
      const parts = line.split('**');
      const formatted = parts.map((part, i) => 
        i % 2 === 1 ? <strong key={i} style={{ color: '#f6c12b' }}>{part}</strong> : part
      );
      elements.push(<p key={index}>{formatted}</p>);
    } else {
      elements.push(<p key={index}>{line}</p>);
    }
  });
  
  return <div>{elements}</div>;
};

// Generate a unique ID from markdown content using SHA-256 hash
// This ensures the same content always produces the same ID
async function generateMarkdownId(markdownContent) {
  // Step 1: Normalize the content (trim whitespace, normalize line endings)
  // This ensures "Hello\nWorld" and "Hello\r\nWorld" produce the same ID
  const normalized = markdownContent.trim().replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Step 2: Convert the text to bytes (UTF-8 encoding)
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  
  // Step 3: Create a SHA-256 hash using the browser's Web Crypto API
  // SHA-256 is a cryptographic hash function that:
  // - Takes any input and produces a fixed-length output (256 bits = 64 hex characters)
  // - The same input ALWAYS produces the same output (deterministic)
  // - Different inputs produce different outputs (collision-resistant)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Step 4: Convert the hash buffer to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  // Step 5: Return first 16 characters as a readable ID
  // Example: "a3f5b2c9d1e4f6a8" instead of the full 64-character hash
  return hashHex.substring(0, 16);
}

// Component to display markdown ID
const MarkdownIdDisplay = ({ markdownContent, onUseId }) => {
  const [markdownId, setMarkdownId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const updateId = async () => {
      if (markdownContent.trim()) {
        setLoading(true);
        try {
          const id = await generateMarkdownId(markdownContent);
          setMarkdownId(id);
        } catch (error) {
          console.error('Error generating markdown ID:', error);
          setMarkdownId('');
        } finally {
          setLoading(false);
        }
      } else {
        setMarkdownId('');
      }
    };

    // Debounce the ID generation
    const timeoutId = setTimeout(updateId, 500);
    return () => clearTimeout(timeoutId);
  }, [markdownContent]);

  if (!markdownId && !loading) return null;

  return (
    <div style={{ 
      fontSize: '0.75rem', 
      color: '#787878',
      fontFamily: 'monospace',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }}>
      {loading ? (
        <span>Generating ID...</span>
      ) : (
        <>
          <span>ID:</span>
          <span style={{ color: '#f6c12b', fontWeight: 600 }}>{markdownId}</span>
          {onUseId && (
            <button
              onClick={() => onUseId(markdownId)}
              style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                background: 'transparent',
                border: '1px solid #f6c12b',
                borderRadius: '0.25rem',
                color: '#f6c12b',
                fontSize: '0.7rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f6c12b';
                e.target.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#f6c12b';
              }}
            >
              Use in Review
            </button>
          )}
        </>
      )}
    </div>
  );
};

// Helper function to convert data URL to file
const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Older stored markdown may contain concrete location text instead of placeholders.
// Normalize to placeholders so selected photo location is always injected dynamically.
const normalizeLocationPlaceholders = (instructions, candidates = {}) => {
  if (typeof instructions !== 'string' || !instructions.trim()) {
    return instructions;
  }

  if (instructions.includes('{state}') || instructions.includes('{county}')) {
    return instructions;
  }

  const stateCandidates = [candidates.photoState, candidates.designState].filter(Boolean);
  const countyCandidates = [candidates.photoCounty, candidates.designCounty]
    .filter(value => value && value !== 'N/A');

  let normalized = instructions;

  // Canonicalize the key metadata lines even if values are stale/unrelated.
  normalized = normalized.replace(
    /\*\*Location:\*\*[^\n]*/gi,
    '**Location:** {county} County, {state}'
  );
  normalized = normalized.replace(
    /\*\*Style Preference Description:\*\*[^\n]*/gi,
    '**Style Preference Description:** {stylePreferenceDescription}'
  );

  countyCandidates.forEach((county) => {
    const escapedCounty = escapeRegExp(county);
    normalized = normalized.replace(new RegExp(`\\b${escapedCounty}\\s+County\\b`, 'gi'), '{county} County');
    normalized = normalized.replace(new RegExp(`\\b${escapedCounty}\\b`, 'gi'), '{county}');
  });

  stateCandidates.forEach((state) => {
    const escapedState = escapeRegExp(state);
    normalized = normalized.replace(new RegExp(`\\b${escapedState}\\b`, 'gi'), '{state}');
  });

  return normalized;
};

const applyInstructionPlaceholders = (instructions, location = {}, stylePreference = '') => {
  const state = location.state || 'N/A';
  const county = location.county || 'N/A';
  return (instructions || '')
    .replace(/{county}/g, county)
    .replace(/{state}/g, state)
    .replace(/{stylePreferenceDescription}/g, stylePreference)
    .replace(/{stylePreference}/g, stylePreference);
};

// Build a template form so markdownId is agnostic to dynamic values.
const toTemplateInstructions = (instructions, location = {}, stylePreference = '') => {
  if (typeof instructions !== 'string' || !instructions.trim()) {
    return instructions;
  }

  const state = location.state || '';
  const county = location.county || '';
  let templated = normalizeLocationPlaceholders(instructions, {
    photoState: state,
    designState: state,
    photoCounty: county,
    designCounty: county
  });

  if (stylePreference) {
    templated = templated.replace(new RegExp(escapeRegExp(stylePreference), 'g'), '{stylePreferenceDescription}');
  }
  if (county && county !== 'N/A') {
    templated = templated.replace(new RegExp(escapeRegExp(county), 'g'), '{county}');
  }
  if (state) {
    templated = templated.replace(new RegExp(escapeRegExp(state), 'g'), '{state}');
  }

  return templated;
};


// Unlimited users list (same as CreateDesignPage)
const UNLIMITED_USERS = ['Co4cpjmwZQb8uWkGtVhqji1VQvq1', 'qyf7gBFJwCMnx9s7UzC69L9FLyf2'];

const PlaygroundPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [environmentPhotos, setEnvironmentPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [stylePreferenceDescription, setStylePreferenceDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatedDesign, setGeneratedDesign] = useState(null);
  const [markdownAnalysis, setMarkdownAnalysis] = useState('');
  const [processingStep, setProcessingStep] = useState('');
  const [generationResult, setGenerationResult] = useState(null); // Store full result from cloud function
  const [resolvedLocation, setResolvedLocation] = useState({ state: null, county: 'N/A' });
  
  // Design toggles
  const [designToggles, setDesignToggles] = useState({
    blobs: false,
    contourLines: false,
    speckledDots: false,
    geometricShapes: false,
    organicObjects: false,
    highContrast: false,
    limitedPalette: false,
    outlineStrokes: false,
    whiteSpace: false
  });
  
  // Rubric scores
  const [rubricScores, setRubricScores] = useState({
    colorPalette: null,
    patternQuality: null,
    functionality: null,
    analysis: null,
    overallQuality: null
  });
  
  const [rubricNotes, setRubricNotes] = useState('');
  const [testName, setTestName] = useState('');
  const [markdownTab, setMarkdownTab] = useState('edit'); // 'edit' or 'preview'
  const [mainTab, setMainTab] = useState('test'); // 'test' or 'review'
  /** Filled from camo_prompt_iterations / camo_prompt_state when a design or active prompt exists */
  const [loadingOriginalMarkdown, setLoadingOriginalMarkdown] = useState(false);
  const [originalMarkdownStatus, setOriginalMarkdownStatus] = useState(null); // 'loaded' | 'none' | 'missing_ref'
  
  // Review tab state
  const [searchMarkdownId, setSearchMarkdownId] = useState('');
  const [reviewResults, setReviewResults] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewMarkdownTab, setReviewMarkdownTab] = useState('preview'); // 'preview' or 'raw'
  
  // Refs for auto-resizing textareas
  const stylePreferenceTextareaRef = useRef(null);
  const markdownEditorRef = useRef(null);
  const rubricNotesTextareaRef = useRef(null);

  // Check if user has access
  useEffect(() => {
    if (currentUser && !UNLIMITED_USERS.includes(currentUser.uid)) {
      // User doesn't have access, show error
      return;
    }
  }, [currentUser]);

  // Auto-resize textareas based on content
  useEffect(() => {
    const textarea = stylePreferenceTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [stylePreferenceDescription]);

  useEffect(() => {
    const textarea = markdownEditorRef.current;
    if (textarea && markdownTab === 'edit') {
      // Small delay to ensure the textarea is visible before calculating
      setTimeout(() => {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }, 0);
    }
  }, [customInstructions, markdownTab]);

  useEffect(() => {
    const textarea = rubricNotesTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [rubricNotes]);

  // Load environment photos from environment_photos collection
  useEffect(() => {
    const loadEnvironmentPhotos = async () => {
      if (!currentUser) return;
      
      try {
        const envPhotosRef = collection(db, 'environment_photos');
        // Get all environment photos ordered by creation date
        // Filter for those with photoUrl on client-side
        const q = query(
          envPhotosRef,
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const photos = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data
            };
          })
          .filter(photo => photo.photoUrl && photo.status !== 'failed'); // Only show photos that are uploaded and not failed
        
        setEnvironmentPhotos(photos);
      } catch (error) {
        console.error('Error loading environment photos:', error);
      }
    };

    loadEnvironmentPhotos();
  }, [currentUser]);

  // When an environment photo is selected, load the active challenger template
  // (camo_prompt_state → camo_prompt_iterations, with legacy markdown_instructions fallback)
  useEffect(() => {
    if (!selectedPhoto?.id) {
      setOriginalMarkdownStatus(null);
      setResolvedLocation({ state: null, county: 'N/A' });
      return;
    }

    let active = true;
    const photoId = selectedPhoto.id;

    const loadOriginalTemplate = async () => {
      setLoadingOriginalMarkdown(true);
      setOriginalMarkdownStatus(null);
      try {
        const photoState = selectedPhoto.state || null;
        const photoCounty = selectedPhoto.county || 'N/A';

        const designsQuery = query(
          collection(db, 'designs'),
          where('environmentPhotoId', '==', photoId)
        );
        const snap = await getDocs(designsQuery);
        if (!active) return;

        if (snap.empty) {
          setResolvedLocation({ state: photoState, county: photoCounty });
          const stateRef = doc(db, 'camo_prompt_state', 'current');
          const stateSnap = await getDoc(stateRef);
          if (!active) return;
          if (stateSnap.exists()) {
            const activeB = stateSnap.data()?.activeB;
            const templateText = activeB?.instructionsTemplate
              || (activeB?.iterationId
                ? (await getDoc(doc(db, 'camo_prompt_iterations', activeB.iterationId))).data()?.instructionsTemplate
                : null);
            if (templateText) {
              setCustomInstructions(normalizeLocationPlaceholders(templateText, {
                photoState, photoCounty, designState: photoState, designCounty: photoCounty,
              }));
              setOriginalMarkdownStatus('loaded');
              return;
            }
          }
          setCustomInstructions('');
          setOriginalMarkdownStatus('none');
          return;
        }

        const designs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        designs.sort((a, b) => {
          const aMs = a.createdAt?.toMillis?.() ?? 0;
          const bMs = b.createdAt?.toMillis?.() ?? 0;
          return bMs - aMs;
        });
        const design = designs[0];
        const designState = design.state || null;
        const designCounty = design.county || 'N/A';

        const effectiveState = photoState || designState;
        const effectiveCounty = (photoCounty && photoCounty !== 'N/A') ? photoCounty : designCounty;
        setResolvedLocation({ state: effectiveState, county: effectiveCounty || 'N/A' });

        const loadTemplateText = async () => {
          const stateRef = doc(db, 'camo_prompt_state', 'current');
          const stateSnap = await getDoc(stateRef);
          if (stateSnap.exists()) {
            const activeB = stateSnap.data()?.activeB;
            if (activeB?.instructionsTemplate) {
              return activeB.instructionsTemplate;
            }
            if (activeB?.iterationId) {
              const iterRef = doc(db, 'camo_prompt_iterations', activeB.iterationId);
              const iterSnap = await getDoc(iterRef);
              if (iterSnap.exists() && iterSnap.data()?.instructionsTemplate) {
                return iterSnap.data().instructionsTemplate;
              }
            }
          }

          const iterationId = design.promptIterationIdB || design.promptIterationIdA;
          if (iterationId) {
            const iterRef = doc(db, 'camo_prompt_iterations', iterationId);
            const iterSnap = await getDoc(iterRef);
            if (iterSnap.exists() && iterSnap.data()?.instructionsTemplate) {
              return iterSnap.data().instructionsTemplate;
            }
          }

          const markdownId = design.markdownIdB || design.markdownIdA;
          if (markdownId) {
            const instrRef = doc(db, 'markdown_instructions', markdownId);
            const instrSnap = await getDoc(instrRef);
            if (instrSnap.exists() && instrSnap.data()?.instructions) {
              return instrSnap.data().instructions;
            }
          }

          return null;
        };

        const templateText = await loadTemplateText();
        if (!active) return;

        if (templateText) {
          const normalizedTemplate = normalizeLocationPlaceholders(
            templateText,
            { photoState, photoCounty, designState, designCounty }
          );
          setCustomInstructions(normalizedTemplate);
          setOriginalMarkdownStatus('loaded');
          return;
        }

        setCustomInstructions('');
        setOriginalMarkdownStatus('missing_ref');
      } catch (e) {
        console.error('Error loading original markdown template:', e);
        if (active) {
          setOriginalMarkdownStatus('error');
        }
      } finally {
        if (active) {
          setLoadingOriginalMarkdown(false);
        }
      }
    };

    loadOriginalTemplate();
    return () => {
      active = false;
    };
  }, [selectedPhoto]);

  const templateInstructionsForId = useMemo(() => {
    const effectiveState = resolvedLocation.state || selectedPhoto?.state || '';
    const effectiveCounty = resolvedLocation.county || selectedPhoto?.county || 'N/A';
    return toTemplateInstructions(
      customInstructions,
      { state: effectiveState, county: effectiveCounty },
      stylePreferenceDescription
    );
  }, [customInstructions, resolvedLocation, selectedPhoto, stylePreferenceDescription]);

  // Check access
  if (!currentUser) {
    return (
      <div className="playground-page">
        <div className="playground-error">
          <h2>Authentication Required</h2>
          <p>Please log in to access the playground.</p>
          <button onClick={() => navigate('/login')}>Go to Login</button>
        </div>
      </div>
    );
  }

  if (!UNLIMITED_USERS.includes(currentUser.uid)) {
    return (
      <div className="playground-page">
        <div className="playground-error">
          <h2>Access Denied</h2>
          <p>You don't have access to the playground. This feature is only available to authorized testers.</p>
          <button onClick={() => navigate('/design')}>Back to Design</button>
        </div>
      </div>
    );
  }

  const handleToggleChange = (toggleName) => {
    setDesignToggles(prev => ({
      ...prev,
      [toggleName]: !prev[toggleName]
    }));
  };

  const handleGenerate = async () => {
    if (!selectedPhoto || !customInstructions.trim() || !stylePreferenceDescription.trim()) {
      return;
    }

    setLoading(true);
    setProcessingStep('generating');

    try {
      const idToken = await currentUser.getIdToken(true);
      const effectiveState = resolvedLocation.state || selectedPhoto.state;
      const effectiveCounty = resolvedLocation.county || selectedPhoto.county || 'N/A';
      
      // Replace placeholders in custom instructions
      const processedInstructions = applyInstructionPlaceholders(
        customInstructions,
        { state: effectiveState || 'N/A', county: effectiveCounty },
        stylePreferenceDescription
      );

      const requestPayload = {
        environmentPhotoUrl: selectedPhoto.photoUrl,
        state: effectiveState || 'N/A',
        county: effectiveCounty,
        userId: currentUser.uid,
        environmentPhotoId: selectedPhoto.id,
        stylePreferenceDescription: stylePreferenceDescription.trim(),
        customMarkdownInstructions: processedInstructions,
        ...(Object.values(designToggles).some(v => v) && { designToggles: designToggles })
      };

      // NOTE: Update this URL after deploying the generateCamoDesignPlayground cloud function
      // The URL format is: https://{function-name}-{region}-{project-id}.a.run.app
      const response = await fetch('https://generatecamodesignplayground-yepdp67tpq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Generation failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.designUrl) {
        setGeneratedDesign(result.designUrl);
        setMarkdownAnalysis(result.markdownAnalysis || '');
        setGenerationResult(result); // Store full result for later use
        setProcessingStep('completed');
      } else {
        throw new Error('No design URL in response');
      }

    } catch (error) {
      console.error('Error generating design:', error);
      setProcessingStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveResult = async () => {
    if (!generatedDesign || !testName.trim()) {
      return;
    }

    setSaving(true);
    try {
      const effectiveState = resolvedLocation.state || selectedPhoto.state;
      const effectiveCounty = resolvedLocation.county || selectedPhoto.county || 'N/A';

      // Upload generated design to storage
      const designFile = dataURLtoFile(generatedDesign, `playground_${Date.now()}.png`);
      const compressedDesign = await imageCompression(designFile, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      });

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      
      const designRef = ref(storage, `playground/${year}/${month}/playground_${Date.now()}.png`);
      await uploadBytes(designRef, compressedDesign);
      const designStorageUrl = await getDownloadURL(designRef);

      // Calculate total score (treat null as 0 for calculation)
      const totalScore = Object.values(rubricScores).reduce((sum, score) => sum + (score || 0), 0);
      const scoredCount = Object.values(rubricScores).filter(score => score !== null).length;
      const averageScore = scoredCount > 0 ? totalScore / scoredCount : 0;

      // Use pre-inserted (template) instructions for markdownId and storage so the same
      // template is not stored per county/state/style
      const processedInstructions = applyInstructionPlaceholders(
        customInstructions,
        { state: effectiveState || 'N/A', county: effectiveCounty },
        stylePreferenceDescription
      );
      const markdownId = await generateMarkdownId(templateInstructionsForId);

      const playgroundRef = collection(db, 'playground_tests');
      const testDocRef = await addDoc(playgroundRef, {
        userId: currentUser.uid,
        testName: testName.trim(),
        environmentPhotoId: selectedPhoto.id,
        environmentPhotoUrl: selectedPhoto.photoUrl,
        generatedDesignUrl: designStorageUrl,
        customMarkdownInstructions: processedInstructions,
        customMarkdownInstructionsTemplate: templateInstructionsForId,
        markdownId: markdownId,
        stylePreferenceDescription: stylePreferenceDescription.trim(),
        designToggles: designToggles,
        markdownAnalysis: markdownAnalysis,
        rubricScores: rubricScores,
        rubricNotes: rubricNotes.trim(),
        averageScore: averageScore,
        totalScore: totalScore,
        state: effectiveState || 'N/A',
        county: effectiveCounty,
        createdAt: serverTimestamp()
      });

      if (averageScore >= 4) {
        const idToken = await currentUser.getIdToken();
        triggerPlaygroundRefinement({
          testId: testDocRef.id,
          userId: currentUser.uid,
          idToken,
        });
      }
      
      // Reset form - clear generated results but keep input fields
      setTestName('');
      setRubricScores({
        colorPalette: null,
        patternQuality: null,
        functionality: null,
        analysis: null,
        overallQuality: null
      });
      setRubricNotes('');
      setGeneratedDesign(null);
      setMarkdownAnalysis('');
      setProcessingStep('');
      setGenerationResult(null);

    } catch (error) {
      console.error('Error saving test result:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSearchByMarkdownIdWithId = async (markdownIdToSearch) => {
    const idToSearch = markdownIdToSearch || searchMarkdownId.trim();
    if (!idToSearch) {
      return;
    }

    setLoadingReviews(true);
    try {
      const playgroundRef = collection(db, 'playground_tests');
      const q = query(
        playgroundRef,
        where('markdownId', '==', idToSearch),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const result = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || null
        };
        
        // Debug: Verify newlines are present when loading
        if (result.customMarkdownInstructions) {
          const newlineCount = (result.customMarkdownInstructions.match(/\n/g) || []).length;
          console.log('Loaded instructions with', newlineCount, 'line breaks');
        }
        
        return result;
      });
      
      setReviewResults(results);
    } catch (error) {
      console.error('Error searching by markdown ID:', error);
      setReviewResults([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleSearchByMarkdownId = () => {
    handleSearchByMarkdownIdWithId();
  };

  return (
    <div className="playground-page">
      <div className="playground-header">
        <h1>Playground - Instruction Testing</h1>
        <p>Test custom markdown instructions for camo pattern generation</p>
      </div>

      {/* Main Tab Navigation */}
      <div className="main-tabs">
        <button
          className={`main-tab ${mainTab === 'test' ? 'active' : ''}`}
          onClick={() => setMainTab('test')}
        >
          Test
        </button>
        <button
          className={`main-tab ${mainTab === 'review' ? 'active' : ''}`}
          onClick={() => setMainTab('review')}
        >
          Review by Markdown ID
        </button>
      </div>

      {mainTab === 'test' ? (
        <div className="playground-content">
        <div className="playground-left-panel">
          {/* Photo Selection */}
          <div className="playground-section">
            <h3>Select Environment Photo</h3>
            <div className="photo-selector">
              {environmentPhotos.length === 0 ? (
                <p className="no-photos">No environment photos found. Create a design first to upload photos.</p>
              ) : (
                <div className="photo-grid">
                  {environmentPhotos.map(photo => (
                    <div
                      key={photo.id}
                      className={`photo-item ${selectedPhoto?.id === photo.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      {photo.photoUrl ? (
                        <img src={photo.photoUrl} alt="Environment" />
                      ) : (
                        <div className="photo-placeholder">No Image</div>
                      )}
                      <div className="photo-info">
                        <p><strong>{photo.state}</strong></p>
                        {photo.county && photo.county !== 'N/A' && <p>{photo.county} County</p>}
                        <p className="photo-date">
                          {photo.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Style Preference */}
          <div className="playground-section">
            <h3>Style Preference Description</h3>
            <textarea
              ref={stylePreferenceTextareaRef}
              value={stylePreferenceDescription}
              onChange={(e) => setStylePreferenceDescription(e.target.value)}
              placeholder="Describe your style preferences for the camouflage pattern..."
              maxLength="500"
            />
            <div className="character-count">{stylePreferenceDescription.length}/500</div>
          </div>

          {/* Design Toggles */}
          <div className="playground-section">
            <h3>Design Elements</h3>
            <div className="toggles-grid">
              {Object.entries(designToggles).map(([key, value]) => {
                // Format label: convert camelCase to Title Case
                const label = key
                  .replace(/([A-Z])/g, ' $1')
                  .trim()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
                
                return (
                  <div
                    key={key}
                    className={`toggle-item ${value ? 'active' : ''}`}
                    onClick={() => handleToggleChange(key)}
                  >
                    <span className="toggle-label">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="playground-center-panel">
          {/* Markdown Editor */}
          <div className="playground-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Custom Markdown Instructions</h3>
              <MarkdownIdDisplay 
                markdownContent={templateInstructionsForId} 
                onUseId={async (id) => {
                  setSearchMarkdownId(id);
                  setMainTab('review');
                  // Auto-search after a brief delay to ensure tab is switched
                  setTimeout(() => {
                    if (id.trim()) {
                      handleSearchByMarkdownIdWithId(id);
                    }
                  }, 100);
                }}
              />
            </div>
            {selectedPhoto && (loadingOriginalMarkdown || originalMarkdownStatus) && (
              <p
                style={{
                  fontSize: '0.85rem',
                  color: '#787878',
                  marginTop: 0,
                  marginBottom: '0.75rem',
                  lineHeight: 1.4
                }}
              >
                {loadingOriginalMarkdown && 'Loading prompt template from the original design…'}
                {!loadingOriginalMarkdown && originalMarkdownStatus === 'loaded' &&
                  'Template loaded from the prompt used when this photo was first generated (prefers Design A). You can edit it below.'}
                {!loadingOriginalMarkdown && originalMarkdownStatus === 'none' &&
                  'No design document was found for this photo — enter instructions manually, or run the main Design flow for this upload first.'}
                {!loadingOriginalMarkdown && originalMarkdownStatus === 'missing_ref' &&
                  'A design exists but the saved prompt template could not be loaded from storage.'}
                {!loadingOriginalMarkdown && originalMarkdownStatus === 'error' &&
                  'Could not load the prompt template. Enter instructions manually or try selecting the photo again.'}
              </p>
            )}
            <div className="markdown-tabs">
              <button
                className={`markdown-tab ${markdownTab === 'edit' ? 'active' : ''}`}
                onClick={() => setMarkdownTab('edit')}
              >
                Raw Markdown
              </button>
              <button
                className={`markdown-tab ${markdownTab === 'preview' ? 'active' : ''}`}
                onClick={() => setMarkdownTab('preview')}
              >
                Preview
              </button>
            </div>
            {markdownTab === 'edit' ? (
              <div className="markdown-editor-container">
                <textarea
                  ref={markdownEditorRef}
                  className="markdown-editor"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="Enter your custom markdown instructions..."
                />
              </div>
            ) : (
              <div className="markdown-preview">
                <div className="markdown-content">
                  <SimpleMarkdownRenderer>{customInstructions}</SimpleMarkdownRenderer>
                </div>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <div className="playground-actions">
            <button
              className="generate-btn"
              onClick={handleGenerate}
              disabled={loading || !selectedPhoto || !customInstructions.trim() || !stylePreferenceDescription.trim()}
            >
              Generate Design
            </button>
          </div>

          {/* Generated Design */}
          {generatedDesign && (
            <div className="playground-section">
              <h3>Generated Design</h3>
              <div className="generated-design-container">
                <img src={generatedDesign} alt="Generated Design" />
              </div>
              {markdownAnalysis && (
                <div className="markdown-analysis-container">
                  <h4>Environment Analysis</h4>
                  <div className="markdown-analysis-content">
                    <SimpleMarkdownRenderer>{markdownAnalysis}</SimpleMarkdownRenderer>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="playground-right-panel">
          {/* Rubric Scoring */}
          <div className="playground-section">
            <h3>Scoring Rubric</h3>
            
            <div className="rubric-item">
              <label>Color Palette (1-99)</label>
              <p className="rubric-description">How well do the colors match the environment?</p>
              <input
                type="number"
                min="1"
                max="99"
                value={rubricScores.colorPalette ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to 2 digits, must be between 1-99
                  if (value === '' || (value.length <= 2 && /^\d+$/.test(value))) {
                    const numValue = value === '' ? null : parseInt(value);
                    if (value === '' || (numValue >= 1 && numValue <= 99)) {
                      setRubricScores(prev => ({
                        ...prev,
                        colorPalette: numValue
                      }));
                    }
                  }
                }}
              />
            </div>

            <div className="rubric-item">
              <label>Pattern Quality (1-99)</label>
              <p className="rubric-description">Is the pattern well-structured and effective?</p>
              <input
                type="number"
                min="1"
                max="99"
                value={rubricScores.patternQuality ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to 2 digits, must be between 1-99
                  if (value === '' || (value.length <= 2 && /^\d+$/.test(value))) {
                    const numValue = value === '' ? null : parseInt(value);
                    if (value === '' || (numValue >= 1 && numValue <= 99)) {
                      setRubricScores(prev => ({
                        ...prev,
                        patternQuality: numValue
                      }));
                    }
                  }
                }}
              />
            </div>

            <div className="rubric-item">
              <label>Functionality (1-99)</label>
              <p className="rubric-description">Does it look like actual camouflage gear?</p>
              <input
                type="number"
                min="1"
                max="99"
                value={rubricScores.functionality ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to 2 digits, must be between 1-99
                  if (value === '' || (value.length <= 2 && /^\d+$/.test(value))) {
                    const numValue = value === '' ? null : parseInt(value);
                    if (value === '' || (numValue >= 1 && numValue <= 99)) {
                      setRubricScores(prev => ({
                        ...prev,
                        functionality: numValue
                      }));
                    }
                  }
                }}
              />
            </div>

            <div className="rubric-item">
              <label>Analysis (1-99)</label>
              <p className="rubric-description">How accurate is the environment analysis?</p>
              <input
                type="number"
                min="1"
                max="99"
                value={rubricScores.analysis ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to 2 digits, must be between 1-99
                  if (value === '' || (value.length <= 2 && /^\d+$/.test(value))) {
                    const numValue = value === '' ? null : parseInt(value);
                    if (value === '' || (numValue >= 1 && numValue <= 99)) {
                      setRubricScores(prev => ({
                        ...prev,
                        analysis: numValue
                      }));
                    }
                  }
                }}
              />
            </div>

            <div className="rubric-item">
              <label>Overall Quality (1-99)</label>
              <p className="rubric-description">Overall assessment of the design</p>
              <input
                type="number"
                min="1"
                max="99"
                value={rubricScores.overallQuality ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to 2 digits, must be between 1-99
                  if (value === '' || (value.length <= 2 && /^\d+$/.test(value))) {
                    const numValue = value === '' ? null : parseInt(value);
                    if (value === '' || (numValue >= 1 && numValue <= 99)) {
                      setRubricScores(prev => ({
                        ...prev,
                        overallQuality: numValue
                      }));
                    }
                  }
                }}
              />
            </div>

            <div className="rubric-total">
              <strong>Total Score: {Object.values(rubricScores).reduce((sum, score) => sum + (score || 0), 0)} / 495</strong>
              <strong>Average: {(() => {
                const scoredCount = Object.values(rubricScores).filter(score => score !== null).length;
                const total = Object.values(rubricScores).reduce((sum, score) => sum + (score || 0), 0);
                return scoredCount > 0 ? (total / scoredCount).toFixed(1) : '0.0';
              })()} / 99</strong>
            </div>

            <div className="rubric-notes">
              <label>Notes</label>
              <textarea
                ref={rubricNotesTextareaRef}
                value={rubricNotes}
                onChange={(e) => setRubricNotes(e.target.value)}
                placeholder="Add any notes about this test..."
              />
            </div>

            <div className="save-section">
              <input
                type="text"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Test name (e.g., 'Traditional Approach v2')"
                maxLength="100"
              />
              <button
                className="save-btn"
                onClick={handleSaveResult}
                disabled={loading || saving || !generatedDesign || !testName.trim()}
              >
                {saving ? 'Saving...' : 'Save Test Result'}
              </button>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <div className="review-content">
          <div className="review-search-section">
            <div className="playground-section">
              <h3>Search by Markdown ID</h3>
              <div className="search-container">
                <input
                  type="text"
                  value={searchMarkdownId}
                  onChange={(e) => setSearchMarkdownId(e.target.value)}
                  placeholder="Enter markdown ID (e.g., a3f5b2c9d1e4f6a8)"
                  className="markdown-id-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchByMarkdownId();
                    }
                  }}
                />
                <button
                  className="search-btn"
                  onClick={handleSearchByMarkdownId}
                  disabled={loadingReviews || !searchMarkdownId.trim()}
                >
                  {loadingReviews ? 'Searching...' : 'Search'}
                </button>
              </div>
              {reviewResults.length > 0 && (
                <div className="results-count">
                  Found {reviewResults.length} test{reviewResults.length !== 1 ? 's' : ''} with this markdown ID
                </div>
              )}
            </div>
          </div>

          {reviewResults.length > 0 && (
            <>
              {/* Markdown Instructions and Average Score Summary */}
              <div className="review-summary-section">
                <div className="playground-section">
                  <h3>Markdown Instructions</h3>
                  <div className="markdown-tabs">
                    <button
                      className={`markdown-tab ${reviewMarkdownTab === 'preview' ? 'active' : ''}`}
                      onClick={() => setReviewMarkdownTab('preview')}
                    >
                      Preview
                    </button>
                    <button
                      className={`markdown-tab ${reviewMarkdownTab === 'raw' ? 'active' : ''}`}
                      onClick={() => setReviewMarkdownTab('raw')}
                    >
                      Raw Markdown
                    </button>
                  </div>
                  <div className="markdown-instructions-display">
                    {reviewMarkdownTab === 'preview' ? (
                      <div className="markdown-content">
                        <SimpleMarkdownRenderer>{reviewResults[0]?.customMarkdownInstructions || 'No instructions found'}</SimpleMarkdownRenderer>
                      </div>
                    ) : (
                      <pre className="markdown-raw-content">{reviewResults[0]?.customMarkdownInstructions || 'No instructions found'}</pre>
                    )}
                  </div>
                  <div className="average-quality-display">
                    <strong>Average Overall Quality: </strong>
                    <span className="average-value">
                      {(() => {
                        const overallScores = reviewResults
                          .map(test => test.rubricScores?.overallQuality)
                          .filter(score => score !== null && score !== undefined);
                        if (overallScores.length === 0) return 'N/A';
                        const average = overallScores.reduce((sum, score) => sum + score, 0) / overallScores.length;
                        return average.toFixed(1);
                      })()}
                    </span>
                    <span className="average-count">
                      {' '}({(() => {
                        const overallScores = reviewResults
                          .map(test => test.rubricScores?.overallQuality)
                          .filter(score => score !== null && score !== undefined);
                        return overallScores.length;
                      })()} scored)
                    </span>
                  </div>
                </div>
              </div>

              {/* Results Grid - Images Only */}
              <div className="review-results">
                <div className="results-grid">
                  {reviewResults.map((test) => (
                    <div key={test.id} className="review-card">
                      <div className="review-card-image">
                        {test.generatedDesignUrl ? (
                          <img src={test.generatedDesignUrl} alt={test.testName || 'Generated Design'} />
                        ) : (
                          <div className="no-image">No Image</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {!loadingReviews && searchMarkdownId.trim() && reviewResults.length === 0 && (
            <div className="no-results">
              <p>No tests found with markdown ID: <code>{searchMarkdownId}</code></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlaygroundPage;
